import React, { lazy, memo, Suspense, useEffect, useMemo } from 'react';
import {
  Routes,
  Route,
  Navigate,
  unstable_HistoryRouter as HistoryRouter,
} from 'react-router-dom';
import { MainContainer } from './App.styles';
import { createBrowserHistory } from 'history';
// Charts lazy loaded to reduce initial bundle (contains recharts + @react-pdf)
import { useLoadScript } from '@react-google-maps/api';
import { allLibraries } from './constants/librariesForGoogleMaps';
import 'react-datepicker/dist/react-datepicker.css';
import localStorageKeys from './constants/localStorageKeys';
import { initViewportTracking } from './utils/analytics';
import api from './api/base';
import { userData } from './api/paths';
import { useDispatch, useSelector } from 'react-redux';
import { updateMapsStatus, updateUser } from './redux/slices/userProfile';
import { RootState } from './redux/store';
import {
  adminPermission,
  chartsPermission,
  rtvPermission,
  searchPermission,
} from './constants/userPermissions';
import PrivateRoute from './pages/PrivateRoute';
import useNotifier from './hooks/useNotifier';
import StaffWidget from './components/StaffWidget';
import { Box } from '@mui/system';
import { CircularProgress } from '@mui/material';
import Login from './pages/Login';
import NewVersionDialog from './components/NewVersionDialog';
import RTVErrorBoundary from './components/RTVErrorBoundary';
import { DesktopOnly } from './components/MobileDesktopOnlyNotice';

// Lazy load pages that aren't needed immediately
const Profile = lazy(() => import('./pages/Profile'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RealTimeView = lazy(() => import('./pages/RealTimeView'));
const Configuration = lazy(() => import('./pages/Configuration'));
const Charts = lazy(() => import('./pages/Charts'));
const ActivateUser = lazy(() => import('./pages/ActivateUser'));
const SysSecurityTerms = lazy(() => import('./pages/SysSecurityTerms'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordConfirm = lazy(() => import('./pages/ResetPasswordConfirm'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const SubscriptionsV2 = lazy(() => import('./pages/SubscriptionsV2'));

export const history = createBrowserHistory();

history.listen(({ location }) => {
  if (typeof window !== 'undefined') {
    (window as any)?.gtag(
      'config',
      process.env.REACT_APP_GOOGLE_ANALYTICS_TAG,
      {
        page_path: location.pathname,
      },
    );
  }
});

const App = () => {
  useNotifier();
  const dispatch = useDispatch();

  const { isLoaded } = useLoadScript({
    id: 'vipr-google-maps',
    googleMapsApiKey: process.env.REACT_APP_MAPS_API as string,
    libraries: allLibraries,
    // Pinned: google removed DrawingManager in v3.65 (weekly channel),
    // which crashed every map page. 3.64 is the last version that ships it.
    // TODO: migrate off DrawingManager before google retires 3.64 (~12 months).
    version: '3.64',
  });

  const permissions = useSelector(
    (store: RootState) => store.user?.user?.permissions,
  );

  const getUserInfo = async () => {
    const { data } = await api.get(userData);
    dispatch(updateUser(data));
  };

  useEffect(() => {
    if (isLoaded) dispatch(updateMapsStatus(isLoaded));
  }, [isLoaded]);

  useEffect(() => {
    const access = localStorage.getItem(localStorageKeys.viprToken);

    if (access) {
      api.defaults.headers.common.Authorization = `Bearer ${access}`;
      getUserInfo();
    }
  }, []);

  // Initialize GA4 viewport tracking on app load
  useEffect(() => {
    initViewportTracking();
  }, []);

  // In order for us to allow user to user URLs for specific pages
  // We need to know if he has permission locally saved for him
  const savedPermissions: string[] = useMemo(() => {
    const perms = localStorage.getItem(localStorageKeys.permissions);
    return JSON.parse(perms || '[]');
  }, []);

  // After we fetch info from BE we will redirect user
  // Or he will just stay on the same page
  const currentPermissions
    = permissions?.map((perm) => perm.action) || savedPermissions;

  const getDefaultRouteComponent = (permissionsList?: any[]) => {
    if (permissionsList?.length) {
      let path = '/dashboard';
      if (
        permissionsList.some(
          (permission) => permission.action === searchPermission,
        )
      ) {
        path = '/dashboard';
      } else if (
        permissionsList.some(
          (permission) => permission.action === rtvPermission,
        )
      ) {
        path = '/real-time-view';
      } else if (
        permissionsList.some(
          (permission) => permission.action === chartsPermission,
        )
      ) {
        path = '/charts';
      }
      return <Navigate to={path} />;
    }
    return <Login />;
  };

  return (
    <HistoryRouter history={history}>
      <MainContainer>
        <NewVersionDialog />
        <StaffWidget />
        <Suspense
          fallback={
            <Box
              width="100%"
              height="100vh"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            <Route
              path="/security-terms"
              element={
                <PrivateRoute>
                  <SysSecurityTerms />
                </PrivateRoute>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <PrivateRoute>
                  <PrivacyPolicy />
                </PrivateRoute>
              }
            />
            <Route
              path="/terms-of-use"
              element={
                <PrivateRoute>
                  <TermsOfUse />
                </PrivateRoute>
              }
            />
            {(currentPermissions?.includes(searchPermission)
              || currentPermissions?.includes(adminPermission)) && (
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
            )}
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <DesktopOnly pageName="Profile">
                    <Profile />
                  </DesktopOnly>
                </PrivateRoute>
              }
            />
            {(currentPermissions?.includes(rtvPermission)
              || currentPermissions?.includes(adminPermission)) && (
              <Route
                path="/real-time-view"
                element={
                  <PrivateRoute>
                    <RTVErrorBoundary>
                      <RealTimeView />
                    </RTVErrorBoundary>
                  </PrivateRoute>
                }
              />
            )}
            {(currentPermissions?.includes(chartsPermission)
              || currentPermissions?.includes(adminPermission)) && (
              <Route
                path="/charts"
                element={
                  <PrivateRoute>
                    <DesktopOnly pageName="Charts">
                      <Charts />
                    </DesktopOnly>
                  </PrivateRoute>
                }
              />
            )}
            {currentPermissions?.includes(adminPermission) && (
              <Route
                path="/configuration"
                element={
                  <PrivateRoute>
                    <DesktopOnly pageName="Configuration">
                      <Configuration />
                    </DesktopOnly>
                  </PrivateRoute>
                }
              />
            )}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/reset-password-confirm"
              element={<ResetPasswordConfirm />}
            />
            <Route path="/activate-user" element={<ActivateUser />} />
            <Route path="/subscriptions" element={<DesktopOnly pageName="Subscriptions"><Subscriptions /></DesktopOnly>} />
            <Route path="/subscriptionsV2" element={<DesktopOnly pageName="Subscriptions"><SubscriptionsV2 /></DesktopOnly>} />
            <Route path="/" element={getDefaultRouteComponent(permissions)} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </MainContainer>
    </HistoryRouter>
  );
};

export default memo(App);
